// Turn raw OCPP meter samples into a few chart series.

const POWER = 'Power.Active.Import';
const OFFERED = 'Power.Offered';
const SOC = 'SoC';
const ENERGY_REG = 'Energy.Active.Import.Register';
const ENERGY_INT = 'Energy.Active.Import.Interval';

const PHASE_COLORS = {
  total: '#007bff',
  L1: '#d97706',
  L2: '#059669',
  L3: '#2563eb',
  offered: '#6c757d',
};

export function toNumber(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

export function parseTime(timestamp) {
  if (timestamp == null || timestamp === '') return null;
  const ms = Date.parse(timestamp);
  return Number.isFinite(ms) ? ms : null;
}

function powerToKw(value, unit) {
  const u = String(unit || 'W').trim().toLowerCase();
  if (u === 'kw') return value;
  return value / 1000;
}

function energyToKwh(value, unit) {
  const u = String(unit || 'Wh').trim().toLowerCase();
  if (u === 'kwh') return value;
  return value / 1000;
}

function formatQuantity(value, unit) {
  if (!Number.isFinite(value)) return '—';
  const digits = Math.abs(value) >= 10 ? 1 : 2;
  const shown = value.toFixed(digits);
  return unit ? `${shown} ${unit}` : shown;
}

export function downsampleMinMax(points, maxPoints = 480) {
  if (!points || points.length <= maxPoints) return points || [];
  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const size = points.length / buckets;
  const out = [];
  for (let i = 0; i < buckets; i += 1) {
    const start = Math.floor(i * size);
    const end = Math.max(Math.floor((i + 1) * size), start + 1);
    const slice = points.slice(start, end);
    if (!slice.length) continue;
    let low = slice[0];
    let high = slice[0];
    slice.forEach((point) => {
      if (point.y < low.y) low = point;
      if (point.y > high.y) high = point;
    });
    const ordered = low.t <= high.t ? [low, high] : [high, low];
    ordered.forEach((point) => {
      const prev = out[out.length - 1];
      if (!prev || prev.t !== point.t || prev.y !== point.y) out.push(point);
    });
  }
  return out;
}

function series(id, label, color, points, extra = {}) {
  const ordered = points.slice().sort((a, b) => a.t - b.t);
  return {
    id,
    label,
    color,
    dashed: Boolean(extra.dashed),
    points: downsampleMinMax(ordered),
  };
}

function peak(points) {
  return points.reduce((max, point) => (point.y > max ? point.y : max), -Infinity);
}

function buildPowerChart(samples, offered) {
  const byPhase = new Map();
  samples.forEach((sample) => {
    const key = sample.phase || 'total';
    if (!byPhase.has(key)) byPhase.set(key, []);
    byPhase.get(key).push(sample);
  });
  if (!byPhase.size && !offered.length) return null;

  const phases = Array.from(byPhase.keys()).sort((a, b) => {
    if (a === 'total') return -1;
    if (b === 'total') return 1;
    return a.localeCompare(b);
  });
  const lines = phases.map((phase) => series(
    phase,
    phase === 'total' ? 'Power' : phase,
    PHASE_COLORS[phase] || '#495057',
    byPhase.get(phase),
  ));
  if (offered.length) {
    lines.push(series('offered', 'Offered', PHASE_COLORS.offered, offered, { dashed: true }));
  }
  const measured = phases.includes('total') ? byPhase.get('total') : samples;
  const top = peak(measured);
  return {
    id: 'power',
    title: 'Power',
    unit: 'kW',
    summary: Number.isFinite(top) ? `Peak ${formatQuantity(top, 'kW')}` : '',
    series: lines,
  };
}

function buildSocChart(samples) {
  if (!samples.length) return null;
  const ordered = samples.slice().sort((a, b) => a.t - b.t);
  const first = ordered[0].y;
  const last = ordered[ordered.length - 1].y;
  return {
    id: 'soc',
    title: 'State of charge',
    unit: '%',
    summary: `${Math.round(first)}% → ${Math.round(last)}%`,
    series: [series('soc', 'SoC', '#0d6efd', ordered)],
  };
}

function buildEnergyChart(registers, intervals) {
  let points = [];
  let summary = '';
  const stationRegister = registers.filter((sample) => !sample.phase);
  const chosenRegisters = stationRegister.length ? stationRegister : registers;
  if (chosenRegisters.length) {
    points = chosenRegisters;
    const ordered = chosenRegisters.slice().sort((a, b) => a.t - b.t);
    const delta = ordered[ordered.length - 1].y - ordered[0].y;
    if (delta >= 0) summary = `${formatQuantity(delta, 'kWh')} delivered`;
  } else if (intervals.length) {
    const ordered = intervals.slice().sort((a, b) => a.t - b.t);
    let sum = 0;
    points = ordered.map((sample) => {
      sum += sample.y;
      return { t: sample.t, y: sum };
    });
    summary = `${formatQuantity(sum, 'kWh')} delivered`;
  }
  if (!points.length) return null;
  return {
    id: 'energy',
    title: 'Energy',
    unit: 'kWh',
    summary,
    series: [series('energy', 'Energy', '#198754', points)],
  };
}

export function buildSessionCharts(rows) {
  const power = [];
  const offered = [];
  const soc = [];
  const registers = [];
  const intervals = [];

  (rows || []).forEach((row) => {
    const t = parseTime(row.timestamp);
    const value = toNumber(row.value);
    if (t == null || value == null) return;
    const measurand = row.measurand || ENERGY_REG;
    const phase = row.phase || '';
    if (measurand === POWER) {
      power.push({ t, y: powerToKw(value, row.unit), phase: phase || 'total' });
    } else if (measurand === OFFERED) {
      offered.push({ t, y: powerToKw(value, row.unit), phase: 'offered' });
    } else if (measurand === SOC) {
      soc.push({ t, y: value, phase: '' });
    } else if (measurand === ENERGY_REG) {
      registers.push({ t, y: energyToKwh(value, row.unit), phase });
    } else if (measurand === ENERGY_INT) {
      intervals.push({ t, y: energyToKwh(value, row.unit), phase });
    }
  });

  return [buildPowerChart(power, offered), buildSocChart(soc), buildEnergyChart(registers, intervals)]
    .filter(Boolean);
}
